import { produce } from 'immer';

import { createError } from '@adv-frontend/error';
import { batchSelectorNotifications } from '@direct-frontend/modules-store';
import { DistributiveOmit, objectMap } from '@direct-frontend/stdlib';

import { $internals as $formInternals, FormActionsMap, FormInstance } from '../form/types';
import { assertFormInitialized } from '../form/utils/assertFormInitialized';
import { assertFormNotSubmitting } from '../form/utils/assertFormNotSubmitting';
import { getFormStoreSingleton } from '../store/store';
import { BaseValidationError } from '../types';

import {
    ArrayFieldInstance,
    ArrayFieldInstanceWithoutAsyncActions,
    BaseArrayField,
    BaseArrayFieldInstance,
} from './createArrayField';
import { BaseObjectField, BaseObjectFieldInstance, ObjectFieldInstance } from './createObjectField';
import {
    BaseUnionConstituent,
    BaseUnionField,
    InferUnionFieldConstituentFieldState,
    InferValueByKeyValue,
    UnionField,
    UnionFieldActionsMap,
    UnionFieldInstance,
} from './createUnionField';
import {
    BasePrimitiveField,
    BasePrimitiveFieldInstance,
    Primitive,
    PrimitiveFieldInstance,
} from './createPrimitiveField';
import {
    $internals,
    BaseField,
    BaseFieldActionsMap,
    BaseFieldSelectorsMap,
    BaseFields,
    BoundFieldActions,
    BoundFieldAsyncActions,
    BoundFieldSelectors,
    FieldActionsMap,
    FieldAsyncActionsMap,
    FieldInstance,
    FieldSelectorsMap,
    InferFieldValue,
    InferFieldsValue,
    SetValueOptions,
} from './types';
import { stringifyFieldIdPath } from './utils/stringifyFieldIdPath';
import { resetField, shouldResetField } from './utils/resetField';
import { validateField } from './validation';
import { bindFieldSelectors } from './bindFieldSelectors';
import { bindFieldAsyncActions } from './utils/bindFieldActions';
import { startFieldReactions, stopFieldReactions } from '../form/reactions';
import { parseFieldKey } from './utils/parseFieldKey';
import { scrollToField } from './scrollToField';
import { createFieldStateGetters } from './utils/createFieldStateGetters';
import { ArrayFieldState, FieldState, ObjectFieldState } from '../store/types';

type FieldCreationOptions = {
    setterOptions?: SetValueOptions;
};

/** Привязывает экшены поля к его текущему значение и заворачивает выполнение экшена в immer */
function bindFieldActions<
    Field extends BasePrimitiveField | BaseObjectField | BaseArrayField | BaseUnionField,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    field,
    fieldName,
    fieldId,
    options,
}: {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: Field;
    fieldName: string;
    fieldId: string;
    options?: FieldCreationOptions['setterOptions'];
}) {
    if (!field.actions) {
        return;
    }

    const { getFieldValue, getFieldState, getFieldContext, getValueByKey, getStateByKey } = getFormStoreSingleton(
        form.name
    );

    return objectMap(field.actions, action => (...args: unknown[]) => {
        assertFormInitialized({
            formName: form.name,
            formId: form.id,
            formInitializationId: form[$formInternals].formInitializationId,
        });
        assertFormNotSubmitting(
            `Can not edit ${field.type} field value, while form is submitting`,
            { formName: form.name, formId: form.id, formInitializationId: form[$formInternals].formInitializationId },
            fieldId,
            options
        );

        const currentValue = getFieldValue({
            formInitializationId: form[$formInternals].formInitializationId,
            fieldId,
        });

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Тип гарантирован публичным интерфейсом
        const nextValue = produce(currentValue, draft => {
            const defaultActionParams = {
                draftValue: draft,
                getContext: () =>
                    getFieldContext({
                        formInitializationId: form[$formInternals].formInitializationId,
                        fieldId,
                    }),
                getState: () =>
                    getFieldState({
                        formInitializationId: form[$formInternals].formInitializationId,
                        fieldId,
                    }),
            };

            const actionParams =
                field.type === 'union'
                    ? {
                          ...defaultActionParams,
                          getValueByKey: (unionKey: Primitive) =>
                              getValueByKey({
                                  formInitializationId: form[$formInternals].formInitializationId,
                                  unionFieldId: fieldId,
                                  unionKey,
                              }),
                          getStateByKey: (unionKey: Primitive) =>
                              getStateByKey({
                                  formInitializationId: form[$formInternals].formInitializationId,
                                  unionFieldId: fieldId,
                                  unionKey,
                              }),
                      }
                    : defaultActionParams;
            return action(actionParams, ...args);
        }) as InferFieldValue<Field>;

        // Переиспользуем set метод внутреннего поля
        const fieldInstance = getFieldInstance({
            form,
            field,
            fieldName,
            fieldId,
            creationOptions: { setterOptions: options },
        });

        if (fieldInstance.type === 'array') {
            fieldInstance.setValues(nextValue);
        } else {
            fieldInstance.setValue(nextValue);
        }
    });
}

/** Возвращает инстанс "примитивного" поля */
function getPrimitiveFieldInstance<
    FieldValue extends Primitive,
    FieldValidationError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<FieldValue>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        PrimitiveFieldInstance<
            FieldValue,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldAsyncActions,
            FieldSelectors,
            FieldName
        >
    >,
    FieldSelectors extends FieldSelectorsMap<FieldValue>,
    FieldName extends string,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    field,
    fieldName,
    fieldId,
    creationOptions,
}: {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: BasePrimitiveField;
    fieldName: FieldName;
    fieldId: string;
    creationOptions?: FieldCreationOptions;
}): PrimitiveFieldInstance<
    FieldValue,
    FieldValidationError,
    FieldContext,
    FieldActions,
    FieldSelectors,
    FieldAsyncActions,
    FieldName
> {
    const {
        addFieldValidationErrors,
        getFieldContext,
        getFieldState,
        getFieldValue,
        setFieldValue,
        touch,
        setParentsStateByKey,
        useFieldContext,
        useFieldState,
        useFieldValue,
    } = getFormStoreSingleton(form.name);

    const fieldIdPath = parseFieldKey(fieldId);

    const createBaseInstance = (
        frozenForm?: FormInstance<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        options?: FieldCreationOptions
    ): DistributiveOmit<
        PrimitiveFieldInstance<
            FieldValue,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors,
            FieldAsyncActions,
            FieldName
        >,
        'asyncActions'
    > => {
        const targetForm = frozenForm ?? form;

        return {
            id: fieldId,
            type: 'primitive' as const,
            name: fieldName,
            path: fieldIdPath,
            form: targetForm,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            selectors: bindFieldSelectors(targetForm, field, fieldId) as BoundFieldSelectors<FieldSelectors>,
            // Так как BoundFieldActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            actions: bindFieldActions({
                form: targetForm,
                field,
                fieldName,
                fieldId,
                options: creationOptions?.setterOptions ?? options?.setterOptions,
            }) as BoundFieldActions<FieldActions>,
            getValue: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldValue({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as FieldValue;
            },
            setValue: fieldValue => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });
                assertFormNotSubmitting(
                    'Can not edit primitive field value, while form is submitting',
                    {
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    },
                    fieldId,
                    options?.setterOptions ?? creationOptions?.setterOptions
                );

                // Серия dispatch ниже даёт одну нотификацию подписчиков вместо четырёх
                batchSelectorNotifications(() => {
                    setFieldValue({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        fieldValue,
                        formFields: targetForm[$formInternals].fields,
                    });
                    validateField({
                        cause: 'change',
                        form: targetForm,
                        field,
                        fieldId,
                    });
                    touch({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                    });
                    setParentsStateByKey({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        formFields: targetForm[$formInternals].fields,
                    });
                });
            },
            getState: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldState({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as FieldState<FieldValue, FieldValidationError>;
            },
            state: createFieldStateGetters(targetForm, fieldId),
            getContext: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldContext({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as FieldContext;
            },
            revalidate: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                validateField({
                    cause: 'revalidate',
                    form: targetForm,
                    field,
                    fieldId,
                });
            },
            addValidationError(error) {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                addFieldValidationErrors({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    validatorType: 'onSubmit',
                    fieldId,
                    validationErrors: [
                        {
                            ...error,
                            meta: {
                                source: {
                                    type: 'field',
                                    fieldKey: fieldId,
                                },
                            },
                        },
                    ],
                });
            },
            addValidationErrors(errors) {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                addFieldValidationErrors({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    validatorType: 'onSubmit',
                    fieldId,
                    validationErrors: errors.map(error => ({
                        ...error,
                        meta: {
                            source: {
                                type: 'field',
                                fieldKey: fieldId,
                            },
                        },
                    })),
                });
            },
            [$internals]: {
                declaration: field,
                useValue: () => {
                    const usedFormInitializationId = targetForm[$formInternals].useFormInitializationId();

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: usedFormInitializationId,
                    });

                    // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                    return useFieldValue({
                        formInitializationId: usedFormInitializationId,
                        fieldId,
                    }) as FieldValue;
                },
                useState: () => {
                    const usedFormInitializationId = targetForm[$formInternals].useFormInitializationId();

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: usedFormInitializationId,
                    });

                    // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                    return useFieldState({
                        formInitializationId: usedFormInitializationId,
                        fieldId,
                    }) as FieldState<FieldValue, FieldValidationError>;
                },
                useContext: () => {
                    const usedFormInitializationId = targetForm[$formInternals].useFormInitializationId();

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: usedFormInitializationId,
                    });

                    // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                    return useFieldContext({
                        formInitializationId: usedFormInitializationId,
                        fieldId,
                    }) as FieldContext;
                },
                touch: () => {
                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    });

                    touch({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                    });
                    setParentsStateByKey({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        formFields: targetForm[$formInternals].fields,
                    });
                },
                scrollToField: () => {
                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    });

                    scrollToField({ formId: targetForm.id, fieldId, formName: targetForm.name });
                },
            },
        };
    };

    const baseInstance = createBaseInstance();

    // Так как BoundFieldAsyncActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const asyncActions = bindFieldAsyncActions(
        form,
        field,
        fieldId,
        // Так как BoundFieldAsyncActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        createBaseInstance as () => unknown as (
            frozenForm?: FormInstance<
                FormName,
                FormFields,
                FormValidationError,
                FormContextInput,
                FormContext,
                FormActions,
                FormSubmitArg,
                FormSubmitReturnValue
            >,
            options?: FieldCreationOptions
        ) => DistributiveOmit<BasePrimitiveFieldInstance, 'asyncActions'>,
        creationOptions?.setterOptions
    ) as BoundFieldAsyncActions<FieldAsyncActions>;

    return {
        ...baseInstance,
        asyncActions,
    };
}

/** Возвращает инстанс "объектного" поля */
function getObjectFieldInstance<
    FieldName extends string,
    FieldValidationError extends BaseValidationError,
    FieldContext,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    field,
    fieldName,
    fieldId,
    creationOptions,
}: {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: BaseObjectField;
    fieldName: FieldName;
    fieldId: string;
    creationOptions?: FieldCreationOptions;
}): ObjectFieldInstance<
    InferFieldsValue<BaseFields>,
    FieldValidationError,
    FieldContext,
    FieldActionsMap<
        InferFieldsValue<BaseFields>,
        ObjectFieldState<InferFieldsValue<BaseFields>, FieldValidationError>,
        FieldContext
    >,
    FieldSelectorsMap<InferFieldsValue<BaseFields>>,
    FieldAsyncActionsMap<InferFieldsValue<BaseFields>>,
    FieldName
> {
    const {
        addFieldValidationErrors,
        getFieldContext,
        getFieldState,
        getFieldValue,
        setFieldValue,
        touch,
        setParentsStateByKey,
        useFieldContext,
        useFieldState,
        useFieldValue,
    } = getFormStoreSingleton(form.name);

    const fieldIdPath = parseFieldKey(fieldId);

    const createBaseInstance = (
        frozenForm?: FormInstance<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        options?: FieldCreationOptions
    ): DistributiveOmit<
        ObjectFieldInstance<
            InferFieldsValue<BaseFields>,
            FieldValidationError,
            FieldContext,
            FieldActionsMap<
                InferFieldsValue<BaseFields>,
                ObjectFieldState<InferFieldsValue<BaseFields>, FieldValidationError>,
                FieldContext
            >,
            FieldSelectorsMap<InferFieldsValue<BaseFields>>,
            FieldAsyncActionsMap<InferFieldsValue<BaseFields>>,
            FieldName
        >,
        'asyncActions'
    > => {
        const targetForm = frozenForm ?? form;
        return {
            id: fieldId,
            type: 'object' as const,
            name: fieldName,
            path: fieldIdPath,
            form: targetForm,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            selectors: bindFieldSelectors(targetForm, field, fieldId) as BoundFieldSelectors<BaseFieldSelectorsMap>,
            fields: objectMap(field.fields, (field, innerFieldName) =>
                getFieldInstance({
                    form: targetForm,
                    field,
                    fieldName: innerFieldName,
                    fieldId: stringifyFieldIdPath([...fieldIdPath, innerFieldName]),
                    creationOptions: creationOptions ?? options,
                })
            ),
            getValue: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldValue({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as InferFieldsValue<FormFields>;
            },
            setValue: fieldValue => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });
                assertFormNotSubmitting(
                    'Can not edit object field value, while form is submitting',
                    {
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    },
                    fieldId,
                    options?.setterOptions ?? creationOptions?.setterOptions
                );

                // Если требуется то перезапускаем реакции и меняем значение
                if (shouldResetField({ form: targetForm, fieldValue, field, fieldId })) {
                    resetField({
                        form: targetForm,
                        field,
                        fieldId,
                        fieldValue,
                    });
                    // Если не требуется, то просто меняем значением
                } else {
                    setFieldValue({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        fieldValue,
                        formFields: targetForm[$formInternals].fields,
                    });
                }

                validateField({
                    cause: 'revalidate',
                    form: targetForm,
                    field,
                    fieldId,
                });
                touch({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                });
                setParentsStateByKey({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                    formFields: targetForm[$formInternals].fields,
                });
            },
            getState: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldState({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as ObjectFieldState<InferFieldsValue<BaseFields>, FieldValidationError>;
            },
            state: createFieldStateGetters(targetForm, fieldId),
            getContext: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldContext({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as FieldContext;
            },
            revalidate: () => {
                validateField({
                    cause: 'revalidate',
                    form: targetForm,
                    field,
                    fieldId,
                });
            },
            addValidationError(error) {
                addFieldValidationErrors({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    validatorType: 'onSubmit',
                    fieldId,
                    validationErrors: [
                        {
                            ...error,
                            meta: {
                                source: {
                                    type: 'field',
                                    fieldKey: fieldId,
                                },
                            },
                        },
                    ],
                });
            },
            addValidationErrors(errors) {
                addFieldValidationErrors({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    validatorType: 'onSubmit',
                    fieldId,
                    validationErrors: errors.map(error => ({
                        ...error,
                        meta: {
                            source: {
                                type: 'field',
                                fieldKey: fieldId,
                            },
                        },
                    })),
                });
            },
            // Так как BoundFieldActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            actions: bindFieldActions({
                form: targetForm,
                field,
                fieldName,
                fieldId,
                options: creationOptions?.setterOptions ?? options?.setterOptions,
            }) as BoundFieldActions<BaseFieldActionsMap>,
            [$internals]: {
                declaration: field,
                useValue: () => {
                    const formInitializationId = targetForm[$formInternals].useFormInitializationId();

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: formInitializationId,
                    });

                    // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                    return useFieldValue({
                        formInitializationId: formInitializationId,
                        fieldId,
                    }) as InferFieldsValue<FormFields>;
                },
                useState: () => {
                    const formInitializationId = targetForm[$formInternals].useFormInitializationId();

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: formInitializationId,
                    });

                    // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                    return useFieldState({
                        formInitializationId: formInitializationId,
                        fieldId,
                    }) as FieldState<InferFieldsValue<BaseFields>, FieldValidationError>;
                },
                useContext: () => {
                    const formInitializationId = targetForm[$formInternals].useFormInitializationId();

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: formInitializationId,
                    });

                    // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                    return useFieldContext({
                        formInitializationId: formInitializationId,
                        fieldId,
                    }) as FieldContext;
                },
                touch: () => {
                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    });

                    touch({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                    });
                    setParentsStateByKey({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        formFields: targetForm[$formInternals].fields,
                    });
                },
                scrollToField: () => {
                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    });

                    scrollToField({ formId: targetForm.id, fieldId, formName: targetForm.name });
                },
            },
        };
    };

    const baseInstance = createBaseInstance();

    // Так как BoundFieldAsyncActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const asyncActions = bindFieldAsyncActions(
        form,
        field,
        fieldId, // Так как BoundFieldAsyncActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        createBaseInstance as () => unknown as (
            frozenForm?: FormInstance<
                FormName,
                FormFields,
                FormValidationError,
                FormContextInput,
                FormContext,
                FormActions,
                FormSubmitArg,
                FormSubmitReturnValue
            >,
            options?: FieldCreationOptions
        ) => DistributiveOmit<BaseObjectFieldInstance, 'asyncActions'>,
        creationOptions?.setterOptions
    ) as BoundFieldAsyncActions<FieldAsyncActionsMap<InferFieldsValue<BaseFields>>>;

    return {
        ...baseInstance,
        asyncActions,
    };
}

/** Ошибка, которая происходит если попытаться обратиться к не проинициализированному элементу поля-массива */
export const ArrayItemNotInitializedError = createError<
    {
        form: { name: string; id: string };
        field: { path: string; index: number } | { path: string; itemId: string };
    },
    // groupBy по path поля-массива — чтобы в EB разные массивы не склеивались в один messageId
    { field: string }
>('ArrayItemNotInitializedError');

/** Возвращает инстанс поля-массива */
function getArrayFieldInstance<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<
        InferFieldValue<InnerField>[],
        ArrayFieldState<InferFieldValue<InnerField>[], FieldValidationError>,
        FieldContext
    >,
    FieldSelectors extends FieldSelectorsMap<InferFieldValue<InnerField>[]>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ArrayFieldInstanceWithoutAsyncActions<
            InnerField,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors
        >
    >,
    FieldName extends string,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    field,
    fieldName,
    fieldId,
    creationOptions,
}: {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: BaseArrayField;
    fieldName: FieldName;
    fieldId: string;
    creationOptions?: FieldCreationOptions;
}): ArrayFieldInstance<
    InnerField,
    FieldValidationError,
    FieldContext,
    FieldActions,
    FieldSelectors,
    FieldAsyncActions,
    FieldName
> {
    const {
        addFieldValidationErrors,
        getArrayItemIdByIndex,
        getFieldContext,
        getFieldState,
        getFieldValue,
        setFieldValue,
        insertArrayItem,
        removeArrayItem,
        touch,
        setParentsStateByKey,
        useFieldContext,
        useFieldState,
        useFieldValue,
    } = getFormStoreSingleton(form.name);

    const fieldIdPath = parseFieldKey(fieldId);

    /** Выбрасывает исключение, если элемент массива по заданному индексу не проинициализирован */
    function assertArrayFieldItemInitialized(index: number) {
        // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        const arrayValue = getFieldValue({
            formInitializationId: form[$formInternals].formInitializationId,
            fieldId,
        }) as InferFieldValue<InnerField>[];

        if (arrayValue && index in arrayValue) {
            return;
        }

        throw new ArrayItemNotInitializedError('Array item not initialized. Missed value', {
            cause: null,
            additional: {
                form: { name: form.name, id: form.id },
                field: { path: fieldId, index },
            },
            groupBy: { field: fieldName },
        });
    }

    // Кеш для fieldAt - хранит созданные инстансы полей по их ID
    const fieldAtCache = new Map<string, FieldInstance<InnerField, FieldName>>();

    const createBaseInstance = (
        frozenForm?: FormInstance<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        options?: FieldCreationOptions
    ): DistributiveOmit<
        ArrayFieldInstance<
            InnerField,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors,
            never,
            FieldName
        >,
        'asyncActions'
    > => {
        const targetForm = frozenForm ?? form;
        return {
            id: fieldId,
            type: 'array' as const,
            name: fieldName,
            path: fieldIdPath,
            form: targetForm,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            selectors: bindFieldSelectors(targetForm, field, fieldId) as BoundFieldSelectors<BaseFieldSelectorsMap>,
            fieldAt: fieldIndex => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });
                assertArrayFieldItemInitialized(fieldIndex);

                const arrayItemId = getArrayItemIdByIndex({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    arrayFieldId: fieldId,
                    index: fieldIndex,
                });

                if (!arrayItemId) {
                    throw new ArrayItemNotInitializedError('Array item not initialized. Field state not found', {
                        cause: null,
                        additional: {
                            form: { name: targetForm.name, id: targetForm.id },
                            field: { path: fieldId, index: fieldIndex },
                        },
                        groupBy: { field: fieldName },
                    });
                }

                if (fieldAtCache.has(arrayItemId)) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- только что проверили значение
                    return fieldAtCache.get(arrayItemId)!;
                }

                // Так как FieldInstance – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                const fieldInstance = getFieldInstance({
                    form: targetForm,
                    field: field.field,
                    fieldName: `${fieldName}.${fieldId}`,
                    fieldId: arrayItemId,
                    creationOptions: creationOptions ?? options,
                }) as FieldInstance<InnerField, FieldName>;

                fieldAtCache.set(arrayItemId, fieldInstance);

                return fieldInstance;
            },
            setValues: values => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });
                assertFormNotSubmitting(
                    'Can not edit array field value, while form is submitting',
                    {
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    },
                    fieldId,
                    options?.setterOptions ?? creationOptions?.setterOptions
                );

                // Если требуется то перезапускаем реакции и меняем значение
                if (shouldResetField({ form: targetForm, fieldValue: values, field, fieldId })) {
                    resetField({
                        form: targetForm,
                        field,
                        fieldId,
                        fieldValue: values,
                    });
                    // Если не требуется, то просто меняем значением
                } else {
                    setFieldValue({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        fieldValue: values,
                        formFields: targetForm[$formInternals].fields,
                    });
                }

                fieldAtCache.clear();

                // Перезапускаем валидацию для всего массива
                validateField({
                    cause: 'revalidate',
                    form: targetForm,
                    field,
                    fieldId,
                });
                // И помечаем touched массив со всеми элементами
                touch({ formInitializationId: targetForm[$formInternals].formInitializationId, fieldId });
                setParentsStateByKey({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                    formFields: targetForm[$formInternals].fields,
                });
            },
            addValue: valueToAdd => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });
                assertFormNotSubmitting(
                    'Can not edit array field value, while form is submitting',
                    {
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    },
                    fieldId,
                    options?.setterOptions ?? creationOptions?.setterOptions
                );

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                const currentValue = getFieldValue({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as InferFieldValue<InnerField>[];

                const lastIndex = currentValue.length;

                // Меняем значение массива и создаем стейты для полей связанных с добавленным значением
                insertArrayItem({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    formFields: targetForm[$formInternals].fields,
                    arrayId: fieldId,
                    value: valueToAdd,
                    insertIndex: lastIndex,
                });

                const itemId = getArrayItemIdByIndex({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    arrayFieldId: fieldId,
                    index: lastIndex,
                });

                if (itemId) {
                    // Запускаем валидацию и touch всех полей выше и ниже по дереву
                    validateField({
                        cause: 'revalidate',
                        form: targetForm,
                        field: field.field,
                        fieldId: itemId,
                    });
                    touch({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        includeChildren: false,
                    });
                    setParentsStateByKey({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        formFields: targetForm[$formInternals].fields,
                    });

                    // И реакции добавленного поля
                    startFieldReactions({
                        form: targetForm,
                        field: field.field,
                        fieldId: itemId,
                    });
                }
            },
            removeValue: (itemId: string) => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });
                assertFormNotSubmitting(
                    'Can not edit array field value, while form is submitting',
                    {
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    },
                    fieldId,
                    options?.setterOptions ?? creationOptions?.setterOptions
                );

                if (!itemId) {
                    throw new ArrayItemNotInitializedError('Array item not initialized', {
                        cause: null,
                        additional: {
                            form: { name: targetForm.name, id: targetForm.id },
                            field: { path: fieldId, itemId },
                        },
                        groupBy: { field: fieldName },
                    });
                }

                // Вызываем touch и стоп реакций до удаления пока мы можем определить всю цепочку вверх и вниз по стейту полей
                if (itemId) {
                    touch({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        includeChildren: false,
                    });
                    setParentsStateByKey({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        formFields: targetForm[$formInternals].fields,
                    });

                    stopFieldReactions({
                        form: targetForm,
                        field: field.field,
                        fieldId: itemId,
                    });

                    fieldAtCache.delete(itemId);
                }

                // Удаляем значение и стейт всех полей, которые связаны с удаленным
                removeArrayItem({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    arrayId: fieldId,
                    itemId,
                    formFields: targetForm[$formInternals].fields,
                });

                // Запускаем валидацию массива и родительских полей
                validateField({
                    cause: 'revalidate',
                    field,
                    form: targetForm,
                    fieldId,
                    // но не запускаем валидацию всех элементом массива при удалении одного из них
                    notRevalidateInnerFields: true,
                });
            },
            insertValue: (indexToInsert, valueToInsert) => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });
                assertFormNotSubmitting(
                    'Can not edit array field value, while form is submitting',
                    {
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    },
                    fieldId,
                    options?.setterOptions ?? creationOptions?.setterOptions
                );

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                const currentValue = getFieldValue({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as InferFieldValue<InnerField>[];

                // Должна быть возможность вставить элемент в конец массива
                if (indexToInsert < 0 || indexToInsert > currentValue.length) {
                    throw new ArrayItemNotInitializedError('Array item not initialized', {
                        cause: null,
                        additional: {
                            form: { name: targetForm.name, id: targetForm.id },
                            field: { path: fieldId, index: indexToInsert },
                        },
                        groupBy: { field: fieldName },
                    });
                }

                // Меняем значение массива и создаем стейты для полей связанных с добавленным значением
                insertArrayItem({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    formFields: targetForm[$formInternals].fields,
                    insertIndex: indexToInsert,
                    arrayId: fieldId,
                    value: valueToInsert,
                });

                const itemId = getArrayItemIdByIndex({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    arrayFieldId: fieldId,
                    index: indexToInsert,
                });

                if (itemId) {
                    // Запускаем валидацию и touch всех полей выше и ниже по дереву
                    validateField({
                        cause: 'revalidate',
                        form: targetForm,
                        field: field.field,
                        fieldId: itemId,
                    });
                    touch({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        includeChildren: false,
                    });
                    setParentsStateByKey({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        formFields: targetForm[$formInternals].fields,
                    });

                    // И реакции добавленного поля
                    startFieldReactions({
                        form: targetForm,
                        field: field.field,
                        fieldId: itemId,
                    });
                }
            },
            replaceValue: (indexToReplace, valueToReplace) => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });
                assertFormNotSubmitting(
                    'Can not edit array field value, while form is submitting',
                    {
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    },
                    fieldId,
                    options?.setterOptions ?? creationOptions?.setterOptions
                );
                assertArrayFieldItemInitialized(indexToReplace);

                const itemId = getArrayItemIdByIndex({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    arrayFieldId: fieldId,
                    index: indexToReplace,
                });

                if (itemId) {
                    // Переиспользуем set метод внутреннего поля
                    const fieldInstance = getFieldInstance({
                        form: targetForm,
                        field: field.field,
                        fieldName: `${fieldName}.${fieldId}`,
                        fieldId: itemId,
                        creationOptions,
                    });

                    if (fieldInstance.type === 'array') {
                        fieldInstance.setValues(
                            valueToReplace,
                            options?.setterOptions ?? creationOptions?.setterOptions
                        );
                    } else {
                        fieldInstance.setValue(
                            valueToReplace,
                            options?.setterOptions ?? creationOptions?.setterOptions
                        );
                    }
                }
            },
            getValue: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldValue({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as InferFieldValue<InnerField>[];
            },
            getState: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldState({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as FieldState<InferFieldValue<InnerField>[], FieldValidationError>;
            },
            state: createFieldStateGetters(targetForm, fieldId),
            getContext: () => {
                assertFormInitialized({
                    formName: targetForm.name,
                    formId: targetForm.id,
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                });

                // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                return getFieldContext({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    fieldId,
                }) as FieldContext;
            },
            revalidate: () => {
                validateField({
                    cause: 'revalidate',
                    form: targetForm,
                    field,
                    fieldId,
                });
            },
            addValidationError(error) {
                addFieldValidationErrors({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    validatorType: 'onSubmit',
                    fieldId,
                    validationErrors: [
                        {
                            ...error,
                            meta: {
                                source: {
                                    type: 'field',
                                    fieldKey: fieldId,
                                },
                            },
                        },
                    ],
                });
            },
            addValidationErrors(errors) {
                addFieldValidationErrors({
                    formInitializationId: targetForm[$formInternals].formInitializationId,
                    validatorType: 'onSubmit',
                    fieldId,
                    validationErrors: errors.map(error => ({
                        ...error,
                        meta: {
                            source: {
                                type: 'field',
                                fieldKey: fieldId,
                            },
                        },
                    })),
                });
            },
            // Так как BoundFieldActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            actions: bindFieldActions({
                form: targetForm,
                field,
                fieldName,
                fieldId,
                options: options?.setterOptions,
            }) as BoundFieldActions<FieldActions>,
            [$internals]: {
                declaration: field,
                useValue: () => {
                    const formInitializationId = targetForm[$formInternals].formInitializationId;

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: formInitializationId,
                    });

                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                    return useFieldValue({
                        formInitializationId: formInitializationId,
                        fieldId,
                    }) as InferFieldValue<InnerField>[];
                },
                useState: () => {
                    const formInitializationId = targetForm[$formInternals].formInitializationId;

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: formInitializationId,
                    });

                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                    return useFieldState({
                        formInitializationId: formInitializationId,
                        fieldId,
                    }) as FieldState<InferFieldValue<InnerField>[], FieldValidationError>;
                },
                useContext: () => {
                    const formInitializationId = targetForm[$formInternals].formInitializationId;

                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: formInitializationId,
                    });

                    // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов и ассертом
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
                    return useFieldContext({
                        formInitializationId: formInitializationId,
                        fieldId,
                    }) as FieldContext;
                },
                touch: () => {
                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    });

                    touch({ formInitializationId: targetForm[$formInternals].formInitializationId, fieldId });
                    setParentsStateByKey({
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                        fieldId,
                        formFields: targetForm[$formInternals].fields,
                    });
                },
                scrollToField: () => {
                    assertFormInitialized({
                        formName: targetForm.name,
                        formId: targetForm.id,
                        formInitializationId: targetForm[$formInternals].formInitializationId,
                    });

                    scrollToField({ formId: targetForm.id, fieldId, formName: targetForm.name });
                },
            },
        };
    };

    const baseInstance = createBaseInstance();

    // Так как BoundFieldAsyncActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const asyncActions = bindFieldAsyncActions(
        form,
        field,
        fieldId,
        // Так как BoundFieldAsyncActions – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        createBaseInstance as () => unknown as (
            frozenForm?: FormInstance<
                FormName,
                FormFields,
                FormValidationError,
                FormContextInput,
                FormContext,
                FormActions,
                FormSubmitArg,
                FormSubmitReturnValue
            >,
            options?: FieldCreationOptions
        ) => DistributiveOmit<BaseArrayFieldInstance, 'asyncActions'>,
        creationOptions?.setterOptions
    ) as BoundFieldAsyncActions<FieldAsyncActionsMap<InferFieldValue<InnerField>[]>>;

    return {
        ...baseInstance,
        asyncActions,
    };
}

/** Ошибка, которая происходит если не удалось найти подходящий под текущее значение элемент юниона */
export const UnionConstituentNotFoundError = createError<{
    form: { name: string; id: string };
    field: { path: string[]; key: Primitive };
}>('UnionConstituentNotFoundError');

/** Возвращает инстанс поля-юниона */
function getUnionFieldInstance<
    Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
    FieldName extends string,
    UnionFieldActions extends UnionFieldActionsMap<Key, UnionConstituents>,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    field,
    fieldName,
    fieldId,
    creationOptions,
}: {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: UnionField<Key, UnionConstituents, UnionFieldActions>;
    fieldName: FieldName;
    fieldId: string;
    creationOptions?: FieldCreationOptions;
}): UnionFieldInstance<Key, UnionConstituents[number], UnionFieldActions, FieldName> {
    const { getFieldValue, setFieldValue, touch, getValueByKey, getStateByKey, setParentsStateByKey } =
        getFormStoreSingleton(form.name);

    const fieldIdPath = parseFieldKey(fieldId);

    const constituents = Object.fromEntries(
        field.constituents.map(constituent => [constituent.fields[field.key].defaultValue, constituent])
    );
    const instances = objectMap(constituents, constituent =>
        getObjectFieldInstance({
            form,
            field: constituent,
            fieldName,
            fieldId,
            creationOptions,
        })
    );

    const actions = objectMap(instances, instance => {
        const unionFieldActions = bindFieldActions({
            form,
            field,
            fieldName,
            fieldId,
            options: creationOptions?.setterOptions,
        });

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions --- TS думает что в actions тип never
        const instanceActions = (instance.actions ?? {}) as BoundFieldActions<UnionConstituents[number]['actions']>;

        return {
            ...instanceActions,
            // union экшены перезаписывают
            ...unionFieldActions,
        };
    });

    function getActualObjectKey() {
        assertFormInitialized({
            formName: form.name,
            formId: form.id,
            formInitializationId: form[$formInternals].formInitializationId,
        });

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- выше есть ассерт
        const fieldValue = getFieldValue({
            formInitializationId: form[$formInternals].formInitializationId,
            fieldId,
        }) as InferFieldsValue<UnionConstituents[number]['fields']>;
        return fieldValue[field.key];
    }

    const result = {
        get key() {
            return getActualObjectKey();
        },
        get actions() {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TS теряет связь с тем, что key - строковый литерал
            const key = getActualObjectKey() as string;

            return actions[key];
        },
        setValue: (fieldValue: InferFieldsValue<UnionConstituents[number]['fields']>) => {
            const nextObjectKey = fieldValue[field.key];
            const constituent = constituents[nextObjectKey];

            // Подходящий элемент юниона может не найтись, если тип значения ключа не является одиночным литералом
            if (!constituent) {
                throw new UnionConstituentNotFoundError(
                    'Failed to find suitable union constituent for current union key',
                    {
                        cause: null,
                        additional: {
                            form: { name: form.name, id: form.id },
                            field: { path: fieldIdPath, key: nextObjectKey },
                        },
                    }
                );
            }

            assertFormInitialized({
                formName: form.name,
                formId: form.id,
                formInitializationId: form[$formInternals].formInitializationId,
            });
            assertFormNotSubmitting(
                'Can not edit union field value, while form is submitting',
                {
                    formName: form.name,
                    formId: form.id,
                    formInitializationId: form[$formInternals].formInitializationId,
                },
                fieldId,
                creationOptions?.setterOptions
            );

            const stateForNextKey = getStateByKey({
                formInitializationId: form[$formInternals].formInitializationId,
                unionFieldId: fieldId,
                unionKey: nextObjectKey,
            });
            const initialValueForNextKey = stateForNextKey ? stateForNextKey.initialValue : fieldValue;

            // Если требуется то перезапускаем реакции и меняем значение
            if (shouldResetField({ form, fieldValue, field, fieldId })) {
                resetField({
                    form,
                    field,
                    fieldValue,
                    fieldId,
                    constituentInitialValue: initialValueForNextKey,
                });
            } else {
                setFieldValue({
                    formInitializationId: form[$formInternals].formInitializationId,
                    fieldId,
                    fieldValue,
                    formFields: form[$formInternals].fields,
                });
            }

            validateField({
                cause: 'revalidate',
                form,
                field: field,
                fieldId,
            });
            touch({
                formInitializationId: form[$formInternals].formInitializationId,
                fieldId,
                includeChildren: Boolean(stateForNextKey),
            });
            setParentsStateByKey({
                formInitializationId: form[$formInternals].formInitializationId,
                fieldId,
                formFields: form[$formInternals].fields,
            });
        },
        getValueByKey: <KeyValue extends InferFieldValue<UnionConstituents[number]['fields'][Key]>>(
            unionKey: KeyValue
        ) => {
            assertFormInitialized({
                formName: form.name,
                formId: form.id,
                formInitializationId: form[$formInternals].formInitializationId,
            });

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions --- типизируем unknown наружу
            return getValueByKey({
                formInitializationId: form[$formInternals].formInitializationId,
                unionFieldId: fieldId,
                unionKey,
            }) as InferValueByKeyValue<Key, KeyValue, UnionConstituents[number]> | undefined;
        },
        getStateByKey: <KeyValue extends InferFieldValue<UnionConstituents[number]['fields'][Key]>>(
            unionKey: KeyValue
        ) => {
            assertFormInitialized({
                formName: form.name,
                formId: form.id,
                formInitializationId: form[$formInternals].formInitializationId,
            });

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- типизируем unknown наружу
            return getStateByKey({
                formInitializationId: form[$formInternals].formInitializationId,
                unionFieldId: fieldId,
                unionKey,
            }) as InferUnionFieldConstituentFieldState<Key, KeyValue, UnionConstituents[number]> | undefined;
        },
        // Явно задаем undefined для сериализатора Redux devtools, иначе возникают ошибки Form not initialized
        toJSON: () => undefined,
    };

    // Proxy не позволяет изменять тип возвращаемого значения, поэтому приводим его к корректному типу вручную
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return new Proxy(result, {
        get(target: Record<PropertyKey, unknown>, property: keyof BaseObjectFieldInstance) {
            if (property in target) {
                return target[property];
            }
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TS теряет связь с тем, что key - строковый литерал
            const key = getActualObjectKey() as string;
            return instances[key]?.[property];
        },
    }) as unknown as UnionFieldInstance<Key, UnionConstituents[number], UnionFieldActions, FieldName>;
}

/** Возвращает инстанс переданного поля в зависимости от его типа */
export function getFieldInstance<
    FieldName extends string,
    CurrentLevelFields extends BaseFields,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    field,
    fieldName,
    fieldId,
    creationOptions,
}: {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: BaseField;
    fieldName: FieldName;
    fieldId: string;
    creationOptions?: FieldCreationOptions;
}): FieldInstance<CurrentLevelFields[FieldName], FieldName> {
    let instance;

    if (field.type === 'object') {
        instance = getObjectFieldInstance({ form, field, fieldName, fieldId, creationOptions });
    }

    if (field.type === 'union') {
        instance = getUnionFieldInstance({ form, field, fieldName, fieldId, creationOptions });
    }

    if (field.type === 'array') {
        instance = getArrayFieldInstance({ form, field, fieldName, fieldId, creationOptions });
    }

    if (field.type === 'primitive') {
        instance = getPrimitiveFieldInstance({ form, field, fieldName, fieldId, creationOptions });
    }
    // Так как FieldInstance – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return instance as FieldInstance<CurrentLevelFields[FieldName], FieldName>;
}
